import React, {useState, useEffect, useRef, useContext} from 'react';
import PropTypes from 'prop-types';
import {createFragmentContainer, graphql} from 'react-relay';
import scrollToComponent from 'react-scroll-to-component';
import {injectIntl, defineMessages} from 'react-intl';

import CircularProgress from '@material-ui/core/CircularProgress';
import Tooltip from '@material-ui/core/Tooltip';

import {FRESHDESK_PAYPAL_CANCELLED_MSG} from '../../../utils/variables';
import {NotificationDialogContext} from '../../context/NotificationDialogContext';

import CheckoutBillTable from './tables/CheckoutBillTable';
import CheckoutOrderingPaymentStepPaymentMethods from './CheckoutOrderingPaymentStepPaymentMethods';
import CheckoutOrderingPaymentStepAddress from './CheckoutOrderingPaymentStepAddress';
import CheckoutOrderingPaymentStepCheckbox from './CheckoutOrderingPaymentStepCheckbox';
import {FormattedMessageWrappedInSpan} from '../../misc';

import {
  BraintreeClientTokenMutation,
  SetOrderPaymentMethodMutation,
} from '../../../mutations';

import {initPaypalButton} from '../../../utils/initPaypalButton';
import {includesPayPal, getRealAmount} from '../../../utils/common';
import {trackEvent} from '../../../utils/ga-tracking';
import {sendError} from '../../../appsignal';

const PAYPAL = ['PayPal', 'VisaViaPayPal', 'MastercardViaPayPal'];

const CheckoutOrderingPaymentStep = (props) => {
  const {cart, isPaypalExpress, onChangeAddress, onPreviousStep} = props;
  const {order, availablePaymentMethods} = cart;
  const hasPaypal = includesPayPal(availablePaymentMethods);

  const notificationDialog = useContext(NotificationDialogContext);

  const paymentMethodDiv = useRef();
  const paymentButtonDiv = useRef();

  const [acceptsPrivacy, setAcceptsPrivacy] = useState(false)
  const [acceptsSgContract, setAcceptsSgContract] = useState(false)
  const [addressCollapsed, setAddressCollapsed] = useState(true)
  const [errorMsgId, setErrorMsgId] = useState('paymentMethodNotDefinedError')
  const [isLoadingPayPalCheckout, setIsLoadingPayPalCheckout] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState(order.payment ? order.payment.paymentMethod : undefined)
  const [paymentRequired, setPaymentRequired] = useState(props.cart.paymentRequired)
  const [showTooltip, setShowTooltip] = useState(false)
  const [braintreeToken, setBraintreeToken] = useState(undefined);

  useEffect(() => {
    scrollToComponent(paymentMethodDiv.current);
  }, []);

  useEffect(() => {
    if (paymentMethod && PAYPAL.includes(paymentMethod.name)) {
      if (braintreeToken) {
        renderPayPalCheckoutBtn();
      } else if (!isLoadingPayPalCheckout) {
        loadBraintreeToken();
      }
    }
  }, [paymentMethod, braintreeToken]);

  const handlePaymentMethodChanged = (pm) => {
    const { cart, relay } = props;
    setShowTooltip(false);
    setPaymentMethod(pm);
    
    // We fire and forget this mutation just to have
    // the chance to know about the selected method earlier.
    SetOrderPaymentMethodMutation.commit(
      relay.environment,
      cart.order.id,
      pm.id,
      (error, updateOrder) => {
        if(error) {
          sendError(error);
        }
      }
    );
  };

  const handleCouponChanged = (isPaymentRequired) => {
    setShowTooltip(false);
    setPaymentRequired(isPaymentRequired);
  };

  const handlePrivacyCheckboxChanged = (event, isInputChecked) => {
    setShowTooltip(false);
    setAcceptsPrivacy(isInputChecked);
  };

  const handleSgContractCheckboxChanged = (event, isInputChecked) => {
    setShowTooltip(false);
    setAcceptsSgContract(isInputChecked);
  };

  const handleCompleteCheckout = (type) => {
    if (paymentRequired && !paymentMethod) {
      setShowTooltip(true);
      setErrorMsgId('paymentMethodNotDefinedError');
      return;
    }
    if (!acceptsPrivacy) {
      setShowTooltip(true);
      setErrorMsgId('privacyCheckboxNotCheckedError');
      return;
    }
    if (!acceptsSgContract) {
      setShowTooltip(true);
      setErrorMsgId('sgContractCheckboxNotCheckedError');
      return;
    }

    if (type === 'pay') {
      props.onNextStep(paymentMethod);
    } else if (type === 'free') {
      props.onConfirmCheckout();
    }
  };

  const handleCloseTooltip = () => {
    setShowTooltip(false);
  };

  const handleShowAddress = () => {
    setAddressCollapsed(!addressCollapsed);
  };

  const shouldShowPaymentBtn = () => {
    if (!paymentRequired) {
      return false;
    }

    if (
      acceptsPrivacy &&
      acceptsSgContract &&
      !props.isPaypalExpress &&
      paymentMethod &&
      PAYPAL.includes(paymentMethod.name)
    ) {
      return false;
    }

    return true;
  }

  const getPayButtonClassName = () => {
    if (
      acceptsPrivacy &&
      acceptsSgContract &&
      paymentMethod
    ) {
      return 'primary';
    }

    // default when payment can't be finished
    return 'primary disabled';
  }

  const getPaypalButtonClassName = () => {
    if (
      acceptsPrivacy &&
      acceptsSgContract &&
      !props.isPaypalExpress &&
      paymentMethod &&
      PAYPAL.includes(paymentMethod.name) &&
      !isLoadingPayPalCheckout
    ) {
      return '';
    }

    // default class
    return 'paypal-checkout-hidden';
  };

  const loadBraintreeToken = () => {
    const {relay} = props;
    if (!braintreeToken) {
      setIsLoadingPayPalCheckout(true);
      BraintreeClientTokenMutation.commit(relay.environment, (error, token) => {
        if (!error) {
          setBraintreeToken(token)
        }
      });
    }
  }

  const renderPayPalCheckoutBtn = () => {
    const {cart, onNextStep, intl} = props;

    setIsLoadingPayPalCheckout(true);

    initPaypalButton('paypal-checkout-btn', braintreeToken, {
      checkoutStep: 2,
      paymentMethod: paymentMethod,
      cart: cart,
      variables: {
        amount: getRealAmount(cart.order.bill.totalBruttoPriceUnpayed),
        currency: cart.totalPrice.currency,
      },
      language: intl.locale,
      onClick: () => paymentButtonDiv.current.scrollIntoView({behavior: 'smooth'}),
      onAuthorize: nonce => onNextStep(paymentMethod, nonce),
      onCancel: () => {
        notificationDialog.onOpenNotificationDialog(FRESHDESK_PAYPAL_CANCELLED_MSG);
        trackEvent('Checkout', 'Canceled payment', 'PayPal payment canceled');
      },
      onReady: () => {
        setIsLoadingPayPalCheckout(false);
      }
    });
  }

  const hasPaymentBtn = shouldShowPaymentBtn();
  return (
    <div>
      {paymentRequired && (
        <CheckoutOrderingPaymentStepPaymentMethods
          ref={paymentMethodDiv}
          availablePaymentMethods={cart.availablePaymentMethods}
          isPaypalExpress={isPaypalExpress}
          selectedPaymentMethod={paymentMethod}
          onPaymentMethodChanged={handlePaymentMethodChanged}
        />
      )}
      {!addressCollapsed && (
        <CheckoutOrderingPaymentStepAddress
          address={order.address}
          onChangeAddress={onChangeAddress}
          onShowAddress={handleShowAddress}
        />
      )}

      <div className="row">
        <div className="col-xs-8 col-sm-3 col-sm-offset-3">
          <h2 className="order-payment-form-overview">
            <FormattedMessageWrappedInSpan
              id="checkoutOrderingPaymentStep.paymentBillTitle"
              defaultMessage="Bill overview"
            />
          </h2>
        </div>
        <div className="col-xs-8 col-sm-2 col-sm-offset-5">
          <div
            className="order-payment-form-address-expand"
            onClick={handleShowAddress}
          >
            {addressCollapsed && (
              <FormattedMessageWrappedInSpan
                id="checkoutOrderingPaymentStep.showAddressButton"
                defaultMessage="Show Address"
              />
            )}
          </div>
        </div>
      </div>
      <CheckoutBillTable
        orderId={order.id}
        bill={order.bill}
        onCouponChanged={handleCouponChanged}
      />
      <CheckoutOrderingPaymentStepCheckbox
        acceptsPrivacy={acceptsPrivacy}
        acceptsSgContract={acceptsSgContract}
        onPrivacyCheckboxChanged={handlePrivacyCheckboxChanged}
        onSgContractCheckboxChanged={handleSgContractCheckboxChanged}
      />
      <div className="row">
        <div
          className="order-payment-form-navigation col-xs-16 col-sm-5 last-sm"
          ref={paymentButtonDiv}
        >
          {hasPaymentBtn && (
            <Tooltip
              title={props.intl.formatMessage(translations[errorMsgId])}
              open={showTooltip}
              onClose={handleCloseTooltip}
              arrow
              disableFocusListener
              disableHoverListener
              disableTouchListener
            >
              <button
                className={getPayButtonClassName()}
                id="button-pay"
                onClick={() => handleCompleteCheckout('pay')}
              >
                {props.intl.formatMessage(translations.nextButton)}
              </button>
            </Tooltip>
          )}
          {!hasPaymentBtn && isLoadingPayPalCheckout && (
            <CircularProgress size={15} thickness={4} />
          )}
          {!hasPaymentBtn && !paymentRequired && (
            <button
              className={getPayButtonClassName()}
              id="button-checkout"
              onClick={() => handleCompleteCheckout('free')}
            >
              <FormattedMessageWrappedInSpan
                id="checkoutOrderingPaymentStep.confirmFreeOrderButton"
                defaultMessage="Buy"
              />
            </button>
          )}
          {hasPaypal && (
            <div className={getPaypalButtonClassName()} id="paypal-checkout-btn" />
          )}
        </div>
        <div className="order-payment-form-navigation col-xs-16 col-sm-5 col-sm-offset-3">
          <div className="checkout-back-button" onClick={onPreviousStep}>
            <FormattedMessageWrappedInSpan
              id="checkoutOrderingPaymentStep.backButton"
              defaultMessage="Back to address"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

const translations = defineMessages({
  paymentMethodNotDefinedError: {
    id: 'checkoutOrderingPaymentStep.paymentMethodNotDefinedError',
    defaultMessage: 'Please select a payment method',
  },
  privacyCheckboxNotCheckedError: {
    id: 'checkoutOrderingPaymentStep.privacyCheckboxNotCheckedError',
    defaultMessage: 'You have to accept our aggreements',
  },
  sgContractCheckboxNotCheckedError: {
    id: 'checkoutOrderingPaymentStep.sgContractCheckboxNotCheckedError',
    defaultMessage: 'You have to accept our contract',
  },
  nextButton: {
    id: 'checkoutOrderingPaymentStep.nextButton',
    defaultMessage: 'Pay now',
  },
});

CheckoutOrderingPaymentStep.propTypes = {
  isPaypalExpress: PropTypes.bool,
  onChangeAddress: PropTypes.func,
  onConfirmCheckout: PropTypes.func,
  onNextStep: PropTypes.func,
  onPreviousStep: PropTypes.func,
  cart: PropTypes.object,
  intl: PropTypes.object,
  relay: PropTypes.object,
};

export {CheckoutOrderingPaymentStep};

export default createFragmentContainer(injectIntl(CheckoutOrderingPaymentStep), {
  cart: graphql`
    fragment CheckoutOrderingPaymentStep_cart on Cart {
      id
      availablePaymentMethods {
        id
        name
      }
      order {
        id
        address {
          ...CheckoutOrderingPaymentStepAddress_address
        }
        bill {
          totalBruttoPrice
          totalBruttoPriceUnpayed
          ...CheckoutBillTable_bill
        }
        payment {
          paymentMethod {
            id
            name
          }
        }
        state
      }
      paymentRequired
      totalPrice
    }
  `,
});
